// Standalone, read-only, mobile-first tournament view rendered for public share
// links (`/view/:id`). It mounts OUTSIDE the auth provider (see `main.tsx`), so
// a spectator never sees a login screen — the tournament id in the URL is the
// only capability needed. Data is polled live by `usePublicTournament`.
//
// Visual style: "sleek dark + glow" — a gradient hero, a frosted sticky tab
// bar, glowing cards and an animated live pulse (see `publicStyles`).

import { useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { isRegistrationOpen, type TournamentStatus } from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { usePublicTournament, type PublicLoadStatus } from './usePublicTournament';
import { usePublicPhotos } from './usePublicPhotos';
import { PublicGroups } from './PublicGroups';
import { PublicBracket } from './PublicBracket';
import { PublicRanks } from './PublicRanks';
import { PublicGallery } from './PublicGallery';
import { RegistrationDialog } from './RegistrationDialog';
import { LanguageSelector } from '../LanguageSelector';
import { ACCENT_GRADIENT, HERO_GRADIENT, livePulse, rise } from './publicStyles';
import type { PublicPhoto } from '../../domain';

type Section = 'groups' | 'bracket' | 'ranks' | 'photos';

const STATUS_LABEL_KEY: Record<TournamentStatus, string> = {
  DRAFT: 'status.notStarted',
  IN_PROGRESS: 'status.live',
  FINISHED: 'status.finished',
};

const STATUS_COLOR: Record<TournamentStatus, 'default' | 'info' | 'success'> = {
  DRAFT: 'default',
  IN_PROGRESS: 'info',
  FINISHED: 'success',
};

interface PublicTournamentViewProps {
  tournamentId: string;
}

export function PublicTournamentView({ tournamentId }: PublicTournamentViewProps) {
  const { t } = useTranslation();
  const { tournament, status, error, lastUpdated, refreshing, refresh } =
    usePublicTournament(tournamentId);
  const { photos } = usePublicPhotos(tournamentId);
  const [section, setSection] = useState<Section>('groups');

  const started =
    tournament?.status === 'IN_PROGRESS' || tournament?.status === 'FINISHED';

  const hasPhotos = photos.length > 0;
  // The Photos tab only exists when there are published photos; if it vanishes
  // (all deleted) while selected, fall back to the groups section.
  const activeSection: Section =
    section === 'photos' && !hasPhotos ? 'groups' : section;

  useEffect(() => {
    if (tournament) document.title = t('docTitle.live', { name: tournament.name });
  }, [tournament, t]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Hero
        tournament={tournament}
        started={started}
        refreshing={refreshing}
        onRefresh={refresh}
      />

      {/* Sticky frosted section tabs (only once play has started) */}
      {status === 'ready' && started && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: 'rgba(14,10,15,0.85)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
            <Tabs
              value={activeSection}
              onChange={(_, value: Section) => setSection(value)}
              variant="fullWidth"
              sx={{
                minHeight: 48,
                '& .MuiTab-root': { fontWeight: 700, minHeight: 48 },
                '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
              }}
            >
              <Tab value="groups" label={t('tabs.groups')} />
              <Tab value="bracket" label={t('tabs.bracket')} />
              <Tab value="ranks" label={t('tabs.ranks')} />
              {hasPhotos && <Tab value="photos" label={t('tabs.photos')} />}
            </Tabs>
          </Container>
        </Box>
      )}

      <Container maxWidth="sm" sx={{ py: 2.5, flexGrow: 1, width: '100%' }}>
        <Body
          status={status}
          error={error}
          started={!!started}
          tournament={tournament}
          section={activeSection}
          photos={photos}
          onRetry={refresh}
        />
      </Container>

      <Box
        component="footer"
        sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', py: 1.25, px: 2, textAlign: 'center' }}
      >
        <LiveFooter status={status} lastUpdated={lastUpdated} />
      </Box>
    </Box>
  );
}

interface HeroProps {
  tournament: ReturnType<typeof usePublicTournament>['tournament'];
  /** True once the group stage has started (IN_PROGRESS or FINISHED). */
  started: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}

/**
 * Top banner of the public view. Once play has started it shows the
 * tournament's branding (logo, name, status). Before then those details are
 * already shown by the pre-start card, so the branding is dropped to avoid
 * repeating it — leaving just the slim control row (language picker + refresh),
 * which the card does not provide.
 */
export function Hero({ tournament, started, refreshing, onRefresh }: HeroProps) {
  const { t } = useTranslation();
  const coverUrl = tournament?.coverUrl ?? null;
  return (
    <Box
      sx={{
        backgroundImage: HERO_GRADIENT,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      <Container maxWidth="sm" sx={{ py: started ? 2 : 1.5 }}>
        {/* Utility row — language picker + refresh. On its own right-aligned
            row so the tournament name below always has the full width and the
            controls never collide with it, at any width or name length. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mb: started ? 2 : 0 }}
        >
          <LanguageSelector />
          <IconButton
            onClick={onRefresh}
            disabled={refreshing}
            aria-label={t('actions.refresh')}
            sx={{
              flexShrink: 0,
              width: 38,
              height: 38,
              color: 'text.primary',
              bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.14)',
              backdropFilter: 'blur(6px)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.09)',
                borderColor: 'rgba(244,63,94,0.5)',
              },
            }}
          >
            {refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Stack>

        {/* Identity row — logo, name and status. Only once play has started;
            before then the pre-start card already shows these details. */}
        {started && (
          <Stack direction="row" alignItems="center" spacing={1.75}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                boxShadow: '0 8px 20px -6px rgba(244,63,94,0.6)',
                ...(coverUrl
                  ? {
                      backgroundImage: `url(${coverUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : { backgroundImage: ACCENT_GRADIENT }),
              }}
            >
              {!coverUrl && <EmojiEventsIcon sx={{ color: '#ffffff' }} />}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{ color: 'primary.main', letterSpacing: 1.5, lineHeight: 1.4, display: 'block' }}
              >
                {t('hero.tournament')}
              </Typography>
              <Typography variant="h6" noWrap sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {tournament?.name ?? t('hero.loading')}
              </Typography>
              {tournament && (
                <Box sx={{ mt: 0.75 }}>
                  <StatusBadge status={tournament.status} />
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </Container>
    </Box>
  );
}

function StatusBadge({ status }: { status: TournamentStatus }) {
  const { t } = useTranslation();
  if (status === 'IN_PROGRESS') {
    return (
      <Chip
        size="small"
        color="info"
        variant="outlined"
        icon={
          <Box
            sx={{
              width: 8,
              height: 8,
              ml: 0.75,
              borderRadius: '50%',
              bgcolor: 'success.main',
              animation: `${livePulse} 2s ease-in-out infinite`,
            }}
          />
        }
        label={t('status.live')}
        sx={{ fontWeight: 700, borderColor: 'rgba(244,63,94,0.5)' }}
      />
    );
  }
  return (
    <Chip
      size="small"
      color={STATUS_COLOR[status]}
      variant="outlined"
      label={t(STATUS_LABEL_KEY[status])}
      sx={{ fontWeight: 700 }}
    />
  );
}

interface BodyProps {
  status: PublicLoadStatus;
  error: string | null;
  started: boolean;
  tournament: ReturnType<typeof usePublicTournament>['tournament'];
  section: Section;
  photos: PublicPhoto[];
  onRetry: () => void;
}

function Body({ status, error, started, tournament, section, photos, onRetry }: BodyProps) {
  const { t } = useTranslation();
  if (status === 'loading') {
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  }

  if (status === 'notfound') {
    return (
      <StateCard
        icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
        title={t('notFound.title')}
        body={t('notFound.body')}
      />
    );
  }

  if (status === 'error' || !tournament) {
    return (
      <Centered>
        <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {error ?? t('error.body')}
          </Alert>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRetry}>
            {t('actions.tryAgain')}
          </Button>
        </Stack>
      </Centered>
    );
  }

  if (!started) {
    return <PreStartCard tournament={tournament} />;
  }

  return (
    <Box key={section} sx={{ animation: `${rise} .28s ease both` }}>
      {section === 'groups' && <PublicGroups tournament={tournament} />}
      {section === 'bracket' && <PublicBracket tournament={tournament} />}
      {section === 'ranks' && <PublicRanks tournament={tournament} />}
      {section === 'photos' && <PublicGallery photos={photos} />}
    </Box>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '46vh',
        py: 4,
      }}
    >
      {children}
    </Box>
  );
}

function StateCard({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <Centered>
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 380,
          borderRadius: 3,
          borderColor: 'rgba(255,255,255,0.08)',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0))',
        }}
      >
        <Box sx={{ color: 'text.secondary', mb: 1 }}>{icon}</Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
      </Paper>
    </Centered>
  );
}

/**
 * Pre-start spectator card. Renders, top to bottom: the cover image, title,
 * scheduled start date, a live countdown to kickoff, the registration CTA and
 * finally the tournament's description. Shown while the tournament is still a
 * DRAFT (before the organiser starts the group stage).
 */
function PreStartCard({
  tournament,
}: {
  tournament: NonNullable<ReturnType<typeof usePublicTournament>['tournament']>;
}) {
  const { t, localeCode } = useTranslation();
  const count = Object.keys(tournament.players).length;
  const startsAt = tournament.startsAt;
  const [registerOpen, setRegisterOpen] = useState(false);
  const registrationOpen = isRegistrationOpen(tournament);
  return (
    <Centered>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          // Wide enough to host the large cover (up to its 600px max).
          maxWidth: 600,
          width: '100%',
          borderRadius: 3,
          borderColor: 'rgba(255,255,255,0.08)',
          backgroundImage:
            'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0))',
        }}
      >
        {tournament.coverUrl ? (
          <Box
            component="img"
            src={tournament.coverUrl}
            alt={t('preStart.coverAlt', { name: tournament.name })}
            sx={{
              // Fluid, mobile-first cover: fills the available width and never
              // overflows, capped at the asset's native 600×600. No pixel floor
              // so it stays within even the narrowest phone viewport.
              width: '100%',
              maxWidth: 600,
              aspectRatio: '1 / 1',
              height: 'auto',
              borderRadius: 3,
              objectFit: 'cover',
              display: 'block',
              mx: 'auto',
              mb: 2,
              boxShadow: '0 16px 40px -16px rgba(0,0,0,0.85)',
            }}
          />
        ) : (
          <Box sx={{ color: 'text.secondary', mb: 1 }}>
            <HourglassEmptyIcon sx={{ fontSize: 40 }} />
          </Box>
        )}
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
          {tournament.name}
        </Typography>

        {startsAt !== null ? (
          <Stack spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
            <Box>
              <Typography
                variant="overline"
                sx={{ color: 'primary.main', letterSpacing: 1.5, display: 'block' }}
              >
                {t('preStart.starts')}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {formatStartDate(startsAt, localeCode)}
              </Typography>
            </Box>
            <Countdown target={startsAt} />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('preStart.noStartTime')}
          </Typography>
        )}

        {registrationOpen && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<HowToRegIcon />}
              onClick={() => setRegisterOpen(true)}
            >
              {t('preStart.register')}
            </Button>
            {tournament.registrationClosesAt !== null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {t('preStart.registrationCloses', {
                  date: formatStartDate(tournament.registrationClosesAt, localeCode),
                })}
              </Typography>
            )}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          {t('preStart.registered', { count })}
        </Typography>

        {tournament.description && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: 3, whiteSpace: 'pre-line' }}
          >
            {tournament.description}
          </Typography>
        )}
      </Paper>

      <RegistrationDialog
        open={registerOpen}
        tournamentId={tournament.id}
        tournamentName={tournament.name}
        onClose={() => setRegisterOpen(false)}
      />
    </Centered>
  );
}

/** Time parts remaining until `target` (epoch ms), clamped at zero. */
function remainingParts(target: number): {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} {
  const total = Math.max(0, target - Date.now());
  const totalSeconds = Math.floor(total / 1000);
  return {
    total,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

/** Live, second-by-second countdown to `target` (epoch ms). */
function Countdown({ target }: { target: number }) {
  const { t } = useTranslation();
  const [parts, setParts] = useState(() => remainingParts(target));

  useEffect(() => {
    setParts(remainingParts(target));
    const timer = setInterval(() => setParts(remainingParts(target)), 1000);
    return () => clearInterval(timer);
  }, [target]);

  if (parts.total <= 0) {
    return (
      <Chip
        color="info"
        variant="outlined"
        label={t('countdown.startingSoon')}
        sx={{ fontWeight: 700 }}
      />
    );
  }

  const units: Array<{ key: string; label: string; value: number }> = [
    { key: 'days', label: t('countdown.days'), value: parts.days },
    { key: 'hrs', label: t('countdown.hours'), value: parts.hours },
    { key: 'min', label: t('countdown.minutes'), value: parts.minutes },
    { key: 'sec', label: t('countdown.seconds'), value: parts.seconds },
  ];

  return (
    <Stack direction="row" spacing={1} justifyContent="center">
      {units.map((unit) => (
        <Box
          key={unit.key}
          sx={{
            minWidth: 60,
            px: 1,
            py: 1.25,
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.1)',
            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0))',
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}
          >
            {String(unit.value).padStart(2, '0')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            {unit.label}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/** Human-readable absolute start date in the spectator's local timezone. */
function formatStartDate(at: number, localeCode: string): string {
  return new Date(at).toLocaleString(localeCode, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function LiveFooter({
  status,
  lastUpdated,
}: {
  status: PublicLoadStatus;
  lastUpdated: number | null;
}) {
  const { t } = useTranslation();
  if (status !== 'ready') {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('footer.engine')}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.85} alignItems="center" justifyContent="center">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: 'success.main',
          animation: `${livePulse} 2s ease-in-out infinite`,
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {lastUpdated
          ? t('footer.liveUpdated', { updated: formatUpdated(lastUpdated, t) })
          : t('footer.live')}
      </Typography>
    </Stack>
  );
}

/** Short "updated X ago" label from an epoch-ms timestamp. */
function formatUpdated(
  at: number,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 10) return t('footer.updatedJustNow');
  if (seconds < 60) return t('footer.updatedSeconds', { count: seconds });
  const minutes = Math.round(seconds / 60);
  return t('footer.updatedMinutes', { count: minutes });
}
